import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import { articleList } from "@/mocks/articles";

export default function NewsDetail() {
  const { id } = useParams();
  const article = articleList.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="mx-auto max-w-3xl px-4 pt-40 pb-24 text-center">
        <Seo title="文章不存在 - 樱花漫画" description="未找到该文章。" />
        <i className="ri-file-unknow-line text-5xl text-foreground-400" aria-hidden="true"></i>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">文章不存在</h1>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          返回资讯
        </Link>
      </div>
    );
  }

  const related = articleList.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <>
      <Seo
        title={`${article.title} - 樱花漫画`}
        description={article.summary}
        canonical={`https://comicflower.github.io/news/${article.id}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: article.title,
          datePublished: article.date,
          dateModified: article.date,
          inLanguage: "zh-CN",
          author: { "@type": "Organization", name: "樱花漫画" },
          articleSection: article.category,
          description: article.summary,
        }}
      />
      <div className="mx-auto max-w-3xl px-4 pt-24 md:px-6 md:pt-28">
        <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-700">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <Link to="/news" className="hover:text-primary-700">资讯</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-800">{article.category}</span>
        </nav>

        <article className="mt-6">
          <span className="whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
            {article.category}
          </span>
          <h1 className="mt-3 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl md:leading-snug">
            {article.title}
          </h1>
          <p className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-edit-box-line" aria-hidden="true"></i>
              樱花漫画编辑部
            </span>
            <span>{article.date}</span>
            <span>{article.readTime} 阅读</span>
          </p>

          <div className="mt-6 overflow-hidden rounded-2xl border border-background-200">
            <img src={article.cover} alt={article.title} className="w-full object-cover" />
          </div>

          <div className="mt-8 space-y-5">
            <p className="text-sm leading-relaxed text-foreground-700">{article.summary}</p>
            {article.content.map((para, i) => (
              <p key={i} className="text-sm leading-loose text-foreground-700">
                {para}
              </p>
            ))}
          </div>
        </article>

        <div className="mt-10 flex items-center justify-between border-t border-background-200 pt-6">
          <span className="text-sm text-foreground-500">分享这篇文章</span>
          <div className="flex items-center gap-2">
            <button type="button" aria-label="分享到微博" className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-primary-500 hover:text-background-50">
              <i className="ri-weibo-line" aria-hidden="true"></i>
            </button>
            <button type="button" aria-label="分享到哔哩哔哩" className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-accent-500 hover:text-background-50">
              <i className="ri-bilibili-fill" aria-hidden="true"></i>
            </button>
            <button type="button" aria-label="复制链接" className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-secondary-500 hover:text-background-50">
              <i className="ri-link" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-12 md:px-6">
        <h2 className="font-heading text-xl font-bold text-foreground-950">相关阅读</h2>
        <div className="mt-5 space-y-3">
          {related.map((a) => (
            <Link
              key={a.id}
              to={`/news/${a.id}`}
              className="group flex gap-4 rounded-2xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200"
            >
              <img src={a.cover} alt={a.title} className="h-20 w-28 shrink-0 rounded-lg object-cover" />
              <div className="min-w-0 flex-1 py-1">
                <span className="whitespace-nowrap rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                  {a.category}
                </span>
                <h3 className="mt-2 line-clamp-2 font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700">
                  {a.title}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}